import type {
  RemoteServerConfig,
  RPCFileInfo,
  RPCRequest,
  RPCResponse,
  RPCResponseExists,
  RPCResponseGet,
  RPCResponseSet,
  RPCResponseStartswith,
} from "@pdfking/content-server/client"
import { EncodeServerJWT } from "@pdfking/content-server/client"
import { RPCEnum } from "@pdfking/content-server/client"
import { type ContentType, type VirtualFile } from "@pdfking/pdfengine"
import { MetaCache } from "@pdfking/storage"
import { Items, type StringGUID } from "@pdfking/tfunc"
import {
  GENERIC_ERROR,
  PERMISSION_ERROR,
} from "@pdfking/translations/enum/errors"
import {
  CreateRemoteFile,
  RequestContent,
} from "@pdfking/workflow/actions/pdfengine/Functions"
import url_read_tracked from "@pdfking/workflow/utils/url_read_tracked"

import type { FileStorage } from "../types/FileStorage"

const fail = (rpc: RPCResponse, status: number) => {
  throw status >= 400 && status < 500 ? PERMISSION_ERROR : GENERIC_ERROR
}

export async function ContentStorage(
  config: RemoteServerConfig
): Promise<FileStorage> {
  const store = MetaCache<VirtualFile>(config.db)

  async function infoToVirtualFile(
    key: string,
    info: RPCFileInfo
  ): Promise<VirtualFile> {
    const mirror = await EncodeServerJWT({ enum: RPCEnum.GET, key }, config)
    const existing = await store.get(info.etag)
    const file_meta = { etag: info.etag }

    if (existing) {
      return {
        ...existing,
        file_meta: { ...existing.file_meta, ...file_meta },
      }
    }

    return CreateRemoteFile({
      type: info.type as ContentType,
      mirrors: [mirror],
      guid: info.etag as StringGUID,
      file_meta,
    })
  }

  const rpc = (req: RPCRequest, response?: Response): Promise<RPCResponse> =>
    EncodeServerJWT(req, config)
      .then(url =>
        response
          ? response.blob().then(data => url_read_tracked(url, { data }))
          : fetch(url)
      )
      .then(response =>
        response.json().then(json => ({
          json: json as RPCResponse,
          status: response.status,
        }))
      )
      .then(({ json, status }) => (json.success ? json : fail(json, status)))

  return {
    async startswith(prefix: string) {
      const res = (await rpc({
        enum: RPCEnum.STARTSWITH,
        prefix,
        deep: true,
      })) as RPCResponseStartswith

      const files: Record<string, VirtualFile> = {}

      for (const [key, info] of Items(res.files)) {
        files[key] = await infoToVirtualFile(key, info)
      }

      return files
    },
    async get(key: string): Promise<undefined | VirtualFile> {
      const res = (await rpc({
        enum: RPCEnum.GET,
        key,
      })) as RPCResponseGet

      if (res.info) return await infoToVirtualFile(key, res.info)

      return undefined
    },
    async set(key: string, content: VirtualFile): Promise<void> {
      const res = await RequestContent(content).then(
        blob =>
          rpc(
            {
              enum: RPCEnum.SET,
              key,
            },
            blob
          ) as Promise<RPCResponseSet>
      )

      if (res.info && content.file_meta.remote_id) {
        store.set(res.info.etag, content)
      }
    },
    async delete(key: string): Promise<void> {
      await rpc({
        enum: RPCEnum.DELETE,
        key,
      })
    },
    async exists(key: string): Promise<boolean> {
      const res = (await rpc({
        enum: RPCEnum.EXISTS,
        key,
      })) as RPCResponseExists

      return res.exists
    },
    async open(names: string[]): Promise<void> {
      await rpc({
        enum: RPCEnum.OPEN,
        names,
      })
    },
    async clear(): Promise<void> {
      await rpc({
        enum: RPCEnum.CLEAR,
      })
    },
  }
}
